/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-12-04 15:53:32
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import './index.scss'
import { DatePicker, Form, message, Select } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import DepartmentMemberPicker from '../../../components/DepartmentMemberPicker'
import { FilterType } from '../../index'
import { fetchPhotosCommonAddress } from '../../../../actions/photoLocation'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { districtsOptionType } from '../../index'
import sensors from '../../../../utils/sensors'
import { fetchPhotoRestoreStatus } from '../../../../actions/main'
// antd 使用 Dayjs 类型替代 RangePickerValue
type RangePickerValue = [Dayjs | null, Dayjs | null] | null
const { RangePicker } = DatePicker
const FormItem = Form.Item
const Option = Select.Option

interface IProps {
  handleChangeFilter(args: Partial<FilterType>): void
  filterData: FilterType
}

const Filter: React.FC<IProps> = ({ handleChangeFilter, filterData }) => {
  const {
    selectedDistricts,
    users: selectedNormalUserIDs,
    userIDList: selectedDepartmentUserIDs,
    departmentIDList: selectedDepartments,
    times
  } = filterData
  const [showMemberPickerModal, setShowMemberPickerModal] = useState(false)
  // 地区数据
  const [districts, setDistricts] = useState<Array<districtsOptionType>>([])
  const dispatch = useAppDispatch()
  const groupState = useAppSelector((state) => state.group)
  const groupMembers = groupState.members
  const isVip = groupState.isVip
  const groupId = groupState.groupId
  const config = useAppSelector((state) => state.main.config)
  const daysLimit = useMemo(
    () => parseInt(config.normal_group_data_limit_days || 90),
    [config]
  )

  useEffect(() => {
    handleFetchDistricts()
  }, [])

  // 请求地区数据
  const handleFetchDistricts = useCallback(() => {
    dispatch(fetchPhotosCommonAddress(groupId)).then((res) => {
      setDistricts(Array.isArray(res.districts) ? res.districts : [])
    })
  }, [dispatch, groupId])

  // 检查90天前照片解冻状态
  const handleFetchPhotoRestoreStatus = useCallback(() => {
    return dispatch(fetchPhotoRestoreStatus({ groupID: groupId }))
  }, [dispatch, groupId])

  // 大于今天的日期禁用
  const disabledDate = useCallback(
    (current) => {
      if (isVip) {
        return current > dayjs().endOf('day')
      } else {
        return (
          current > dayjs().endOf('day') ||
          current <
            dayjs()
              .add(0 - daysLimit, 'days')
              .startOf('day')
        )
      }
    },
    [isVip, config, daysLimit]
  )

  // 时间范围变化时
  const handleDateRangeChange = useCallback(
    async ([start, end]) => {
      const isDatesOutOfMaxRange = Math.abs(start.diff(end, 'days', true)) > 31
      if (isDatesOutOfMaxRange) {
        message.warning('筛选时间范围不能超过31天')
        return
      }

      //vip团队且时间超90天，检查解冻状态： 完成 ? 提交选择时间 : 无法提交时间
      //否则 提交选择时间
      if (isVip && dayjs().diff(start, 'days', true) > daysLimit) {
        const restoreStatus = await handleFetchPhotoRestoreStatus()
        if (restoreStatus.status === 0) {
          handleChangeFilter({ times: { start, end } })
        }
      } else {
        handleChangeFilter({ times: { start, end } })
      }
    },
    [handleChangeFilter, handleFetchPhotoRestoreStatus, isVip, daysLimit]
  )

  // 切换地区选择
  const handleChangeDistricts = useCallback(
    (key: string) => {
      const selectItem = districts.find((item) => item.adcode === key)
      handleChangeFilter({ selectedDistricts: selectItem })
    },
    [districts, handleChangeFilter]
  )

  // 计算已选成员
  const handleCalcPickedMemberNum = useCallback(() => {
    const userNumInDepartments = selectedDepartments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total =
      selectedNormalUserIDs.length +
      selectedDepartmentUserIDs.length +
      userNumInDepartments
    const hasSelection =
      selectedDepartments.length > 0 ||
      selectedNormalUserIDs.length > 0 ||
      selectedDepartmentUserIDs.length > 0
    if (
      (!hasSelection && total === 0) ||
      total === groupMembers.length ||
      selectedDepartments.some((department) => department.id === '0')
    ) {
      return groupMembers.length
    } else {
      return total
    }
  }, [
    selectedNormalUserIDs,
    selectedDepartments,
    selectedDepartmentUserIDs,
    groupMembers
  ])

  // 组合已选成员数量进行展示
  const memberPickedValue = useMemo(() => {
    const pickedMembersNum = handleCalcPickedMemberNum()
    if (pickedMembersNum === groupMembers.length) {
      return `全部成员（${groupMembers.length}人）`
    }
    return `已选${pickedMembersNum}人`
  }, [handleCalcPickedMemberNum, groupMembers])

  const handleMemberChangeV2 = useCallback(
    (params) => {
      setShowMemberPickerModal(false)
      handleChangeFilter({
        users: params.selectedNormalUserIDs,
        userIDList: params.selectedDepartmentUserIDs,
        departmentIDList: params.selectedDepartments
      })
    },
    [handleChangeFilter]
  )

  // 点击上报
  const sensorsClick = useCallback(
    (clickItem) => {
      sensors.track('web_click_page_photo_location', {
        clickItem,
        locationName: '',
        groupID: groupId,
        isGroupVIP: isVip
      })
    },
    [isVip, groupId]
  )

  return (
    <div className="photo-location-filter">
      <Form layout="inline">
        <FormItem label="城市">
          <Select
            style={{ width: '236px', marginRight: 30 }}
            value={selectedDistricts.adcode}
            onChange={handleChangeDistricts}
            onDropdownVisibleChange={(open) => {
              open && sensorsClick('chooseLocation')
            }}
          >
            {districts.map((item) => (
              <Option value={item.adcode} key={item.adcode}>
                {item.name}
              </Option>
            ))}
          </Select>
        </FormItem>
        <FormItem label="筛选成员">
          <Select
            style={{ width: '236px', marginRight: 30 }}
            value={memberPickedValue}
            open={false}
            onDropdownVisibleChange={(open) => {
              setShowMemberPickerModal(true)
              open && sensorsClick('choosePhotoMember')
            }}
          />
        </FormItem>
        <FormItem label="筛选时间">
          <RangePicker
            style={{ width: '236px' }}
            allowClear={false}
            value={[times.start, times.end] as RangePickerValue}
            disabledDate={disabledDate}
            onChange={handleDateRangeChange as any}
            onOpenChange={(open) => {
              open && sensorsClick('choosePhotoDate')
            }}
          />
        </FormItem>
      </Form>
      <DepartmentMemberPicker
        open={showMemberPickerModal}
        filterObj={{
          members: selectedNormalUserIDs, // 已选普通成员
          userDepartmentIDs: selectedDepartmentUserIDs, // 已选部门成员
          departments: selectedDepartments // 已选部门
        }}
        onOk={handleMemberChangeV2}
        onCancel={() => setShowMemberPickerModal(false)}
      />
    </div>
  )
}

export default Filter
